/**
 * 设备检测工具
 */

/**
 * 检测是否为 iOS 设备
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(userAgent)
}

/**
 * 检测是否为 Android 设备
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /android/.test(userAgent)
}

/**
 * 检测是否为移动设备
 */
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /mobile|android|iphone|ipad|ipod/.test(userAgent)
}

/**
 * 检测是否为 Safari 浏览器
 */
export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /safari/.test(userAgent) && !/chrome/.test(userAgent)
}

/**
 * 检测是否为 iOS Safari 浏览器
 */
export const isIOSSafari = (): boolean => {
  return isIOS() && isSafari()
}

/**
 * 检测是否支持 PWA 安装
 */
export const canInstallPWA = (): boolean => {
  if (typeof window === 'undefined') return false
  
  // 检查是否已经安装
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return false
  }
  
  // 检查是否支持 beforeinstallprompt 事件（Android Chrome）
  if ('onbeforeinstallprompt' in window) {
    return true
  }
  
  // 检查是否为 iOS Safari
  if (isIOSSafari()) {
    return true
  }
  
  return false
}


export const isPWAInstalled = () => {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  )
}
/**
 * 获取设备信息
 */
export const getDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return {
      isIOS: false,
      isAndroid: false,
      isMobile: false,
      isSafari: false,
      isIOSSafari: false,
      canInstallPWA: false,
      userAgent: ''
    }
  }
  
  return {
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobile(),
    isSafari: isSafari(),
    isIOSSafari: isIOSSafari(),
    canInstallPWA: canInstallPWA(),
    isPWAInstalled: isPWAInstalled(),
    userAgent: window.navigator.userAgent
  }
}

/**
 * 获取终端类型
 * 返回值说明：
 * 1 - 苹果H5
 * 2 - 安卓H5
 * 3 - PWA
 * 4 - APK
 * 5 - 苹果包
 * 6 - 苹果书签
 * 7 - APK上架包
 * 8 - 电脑系统
 */
export const getTerminalType = (channelId?: string): number => {
  if (typeof window === 'undefined') {
    return 8 // 默认返回电脑系统
  }

  // 声明 window 上的类型（如果还没有声明的话）
  const platformGame = (window as any).platformGame
  const jsBridge = (window as any).jsBridge

  // 1. 首先检查是否是原生应用（优先级最高）
  if (platformGame || jsBridge) {
    if (isAndroid()) {
      // Android 原生应用
      // 判断是否是上架包：可以通过 channelId 或 platformGame 中的特殊标识来判断
      // 这里提供一个判断示例，实际可以根据业务需求调整
      // 例如：如果 channelId 是特定值（如应用商店的渠道ID），则认为是上架包
      const isStorePackage = channelId && 
        (channelId.includes('store') || 
         channelId.includes('play') || 
         channelId.includes('market') ||
         platformGame?.isStorePackage === true)
      
      if (isStorePackage) {
        return 7 // APK上架包
      }
      return 4 // APK（普通APK）
    } else if (isIOS()) {
      return 5 // 苹果包
    }
  }

  // 2. 检查是否是苹果书签（iOS Safari 添加到主屏幕）
  // 需要在PWA判断之前，因为iOS Safari书签也会显示为standalone模式
  if (isIOS() && isSafari()) {
    // iOS Safari 书签添加到主屏幕后，navigator.standalone 为 true
    // 且没有Service Worker（PWA通常有Service Worker）
    if ((window.navigator as any).standalone === true) {
      const hasServiceWorker = 'serviceWorker' in navigator && 
        (navigator as any).serviceWorker?.controller !== null
      
      // 如果standalone为true但没有Service Worker，则认为是书签
      // 注意：PWA通常会有Service Worker，而iOS Safari书签没有
      if (!hasServiceWorker) {
        return 6 // 苹果书签
      }
    }
  }

  // 3. 检查是否是PWA
  // isPWAInstalled 已经检查了 display-mode: standalone/fullscreen/minimal-ui
  if (isPWAInstalled()) {
    return 3 // PWA
  }

  // 4. 检查是否是移动设备H5
  if (isMobile()) {
    if (isIOS()) {
      return 1 // 苹果H5
    } else if (isAndroid()) {
      return 2 // 安卓H5
    }
  }

  // 5. 默认返回电脑系统
  return 8 // 电脑系统
} 